import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { textField, ripple } from 'material-components-web';
import { LoaderService } from '../../shared/services/loader.service';
import { ShareDataService } from '../../shared/services/share-data.service';
import { EncryptService } from '../../shared/services/encrypt.service';
import { LoginService } from '../../shared/services/login.service';
import { TokenManagementService } from '../../shared/services/token-management.service';
import { AlertService } from '../../shared/services/alert.service';
import { Guid } from 'guid-typescript';
import { AppSessionService } from '../../shared/services/app-session.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConstantsService } from '@app/common/constants.service';
import { SESSION_STORAGE, StorageService } from 'ngx-webstorage-service';
import { TokenService } from '../../shared/services/token.service';
import { environment } from '../../../environments/environment';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { IDnaAuth } from '@app/shared/model/dna-auth.interface';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatCard } from '@angular/material/card';
import { MatIcon } from "@angular/material/icon";
import { UtilityService } from '@app/common/utility.service';

@Component({
  standalone: true,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, HttpClientModule, CommonModule, MatCard, MatIcon]
})
export class LoginComponent implements OnInit {
  private readonly invalidSessionLogoutMessage = "You have been logged out due to an invalid session. This may have occurred because you logged out from another session or your session has exceeded the maximum duration of 12 hours. Please close your browser and log in again.";
  form: FormGroup;
  emailPattern: string = "^[^\\[\\]!&%@\{\\}\?|#<>+=;:/,*~`\\$\\(\\)]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$"; //NOSONAR
  applicationName: string = "";
  isPreLogin: boolean = false;
  isAccessRevoked: boolean = false;
  isLoginError: boolean = false;
  supportEmail: boolean = false;
  authMode: string = "REPO";
  welcomeScreenTitle: string = "Welcome to ";
  welcomeScreenSubtitle: string = "Lets Get Started";
  CurrentErrorMsg = "";
  userInfo: any;
  isAccessDenied: boolean;
  isAccessDeniedCustomMessage: boolean;
  isSAML: boolean = true;
  accessDeniedSupportEmailAddress: string = "";
  logout: boolean = false;
  errorMsgForAccessRevoked: string;
  errorMsg: string; 
  messageVisible: boolean = false;
  inProgress: boolean = false;
  isSessionExpired: boolean = false;
  sessionExpiredMessage: string ;
  invalidBrowserSessionMessage: string;
  isInvalidBrowserSession: boolean = false;
  constructor(private loader: LoaderService,
    private readonly loginService: LoginService,
    private readonly tokenManagementService: TokenManagementService,
    private readonly tokenService: TokenService,
    private readonly alertService: AlertService,
    private readonly sessionService: AppSessionService,
    private readonly router: Router,
    public constantsService: ConstantsService,
    @Inject(SESSION_STORAGE) private readonly storage: StorageService,
    private readonly encryptService: EncryptService,
    private route: ActivatedRoute,
    private readonly destroyRef: DestroyRef,
    private readonly utilityService : UtilityService) {

  }
handleSessionExpired = (event: any) => {
    if (event instanceof CustomEvent) {
      if (event.detail) {
        this.isSessionExpired = event.detail.isSessionExpired;
        this.sessionExpiredMessage = event.detail.message?.trim() || this.invalidSessionLogoutMessage;
        if (this.isSessionExpired) {
          this.isInvalidBrowserSession = false;
          this.invalidBrowserSessionMessage = '';
        }
      } 
    } 
  }
  handleInvalidBrowserSession = (event: any) => {
    if (event instanceof CustomEvent) {
      if (event.detail) {
        if (this.isSessionExpired && event.detail.isInvalidBrowserSession) {
          return;
        }
        this.isInvalidBrowserSession = event.detail.isInvalidBrowserSession;
        this.invalidBrowserSessionMessage = event.detail.message;
      } 
    } 
  }
  ngOnInit() {
  window.addEventListener('session_expired', this.handleSessionExpired);
  window.addEventListener('invalid_browser_session', this.handleInvalidBrowserSession);
  if ((window as any)._sessionExpiredData) {
      this.isSessionExpired = (window as any)._sessionExpiredData.isSessionExpired;
      if(this.isSessionExpired) {
        this.sessionExpiredMessage = this.invalidSessionLogoutMessage;
       }
      delete (window as any)._sessionExpiredData;
    }
    if ((window as any)._invalidBrowserSessionData && !this.isSessionExpired) {
      this.isInvalidBrowserSession = (window as any)._invalidBrowserSessionData.isInvalidBrowserSession;
      if(this.isInvalidBrowserSession) {
        this.invalidBrowserSessionMessage = "Your session has expired, or the Browser session ID is invalid. Please close your browser and log in again to proceed.";
       }
    }
    delete (window as any)._invalidBrowserSessionData;
    this.form = new FormGroup({
      username: new FormControl(null, [Validators.required, Validators.pattern(this.emailPattern)]),
      currentSecureKey: new FormControl(null, Validators.required),
    });
    this.applicationName = this.constantsService.applicationName;
    // Call the method to handle the asynchronous operation
    this.handleAsyncOperation();

    //SET AUTH MODE SAML AZURE FOR SAML REPO
    this.authMode = environment.AUTH_MODE;

    const params = this.getParams();
    if(params){
      this.loginAgain(params);
    }

    const isUserLoggedOut = this.storage.get("IsUserLoggedIn") === "false";
    const logoutContext = this.utilityService.getSessionLogoutContext();
    const currentTabId = this.utilityService.getTabInstanceId();
    const isLoggedOutFromAnotherTab = logoutContext?.reason === 'inactivity' && logoutContext?.tabId !== currentTabId;
    const isInvalidSessionLogout = logoutContext?.reason === 'invalid_session';
    const isManualLogoutFromAnotherTab = logoutContext?.reason === 'manual_logout' && logoutContext?.tabId !== currentTabId;

    if (isUserLoggedOut) {
      // Priority: if logout came from another tab/session invalidation, always show invalid-session banner.
      if (!this.isSessionExpired && (isLoggedOutFromAnotherTab || isInvalidSessionLogout || isManualLogoutFromAnotherTab)) {
        this.isSessionExpired = true;
        this.sessionExpiredMessage = this.invalidSessionLogoutMessage;
        this.isInvalidBrowserSession = false;
        this.invalidBrowserSessionMessage = '';
      } else if (!this.isSessionExpired && !this.isInvalidBrowserSession) {
        this.errorMsg = 'You have been logged out!';
        this.messageVisible = true;
      }
      this.storage.remove("IsUserLoggedIn"); // Remove flag so it doesn't show again
    }
    
    if (this.storage.get("SessionError") === "true") {
      this.errorMsg = 'Your session has expired, or the session ID is invalid. Please close your browser and log in again to proceed.';
      this.messageVisible = true;
      this.storage.remove("SessionError"); // Remove flag so it doesn't show again
    }

  }

  closeLogoutMessage() {
    this.messageVisible = false;
  }
  redirectToLogin() {
    this.isPreLogin = false;
    if (this.authMode == "REPO") {
      this.handleAsyncOperation();
    } else {
      this.login();
    }
  }

  login(){

    const params = this.getParams();
    if (params) {
      this.loginAgain(params);
    }
    else {
      //Send request to AzureAD for authentication and token authorization in ID Service
      let solutionUrl = encodeURI(location.origin);
      window.location.href = environment.API_AUTH_PATH + '/api/External/Challenge?provider=AzureAD&solutionUrl=' + solutionUrl;
      return;
    }
  }

  loginAgain(params : string) {
    //Remove token from URL
    //Handle token from AzureAD and provide user access to Release
    const decoded = JSON.parse(decodeURIComponent(params)) as IDnaAuth;

    if(decoded.isValidUser && decoded.isAuthenticated ){
          this.inProgress=true;
          this.tokenManagementService.getTokens(decoded.username).subscribe({ next:
          (res) => {
            this.tokenService.delete_cookieDomain();
            let browserSessionId = this.sessionService.getBrowserSession();
            if (!browserSessionId) {
              browserSessionId = Guid.create().toString();
              window.sessionStorage.setItem("browserSessionId", browserSessionId);
            }
            this.tokenService.setToken(res.access_token, res.refresh_token, decoded.username).pipe(
              takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
              this.tokenService.startOrRestartRefreshTokenTimer();
              this.router.navigate(['release']);
              this.inProgress=false;
            });
          }, error:
          (_error) => {
              this.inProgress=false;
            this.tokenService.delete_cookieDomain();
            this.alertService.buildDialog().fire({
              title: 'Error!',
              text: "Error generating token token.",
              icon: this.alertService.ErrorIcon,
              showCancelButton: false,
              confirmButtonText: "OK",
              cancelButtonText: "",
            });
            this.tokenService.removeToken();
          }
      });
    }
        else if(!decoded.isValidUser){
      this.tokenService.delete_cookieDomain();
          this.isAccessRevoked= true;
          this.messageVisible = true;
      this.errorMsg = "You do not have permission to access the application. Please contact administrator.";
      this.tokenService.removeToken();
    }
        else{
      this.tokenService.delete_cookieDomain();
      this.alertService.buildDialog().fire({
        title: 'Error!',
        text: "Error occurred while authenticating user.",
        icon: this.alertService.ErrorIcon,
        showCancelButton: false,
        confirmButtonText: "OK",
        cancelButtonText: "",
      });
      this.tokenService.removeToken();
    }
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    setTimeout(() => {
      [].map.call(document.querySelectorAll('.text-material'), function (element) {
        textField.MDCTextField.attachTo(element);
      });

      [].map.call(document.querySelectorAll('.ripple-material'), function (el) {
        ripple.MDCRipple.attachTo(el);
      });
    }, 2000);
    this.loader.showSpinner.next(false);
  }


  // on submit login form
  onSubmit() {
    if (this.form.invalid) {
      return;
    }
    this.loader.showSpinner.next(true);
    const encUserName = this.encryptService.encryptString(this.form.get('username').value, environment.sessionPropEncKey);
    const newUser = {
      name: encUserName,
      password: this.form.get('currentSecureKey').value
    } as any;
    this.loginService.authenticate(newUser).subscribe({ next:
        (res) => {
          if (res.isSuccess) {
            this.loginSuccess(res);
          } else {
            this.alertService.buildDialog().fire({
              title: this.titleText(res),
              text: res.validationMessage ? res.validationMessage : "The User (email) or Password is incorrect",
              icon: this.icon(res),
              showCancelButton: false,
              confirmButtonText: "OK",
              cancelButtonText: "",
            });
          }
          this.form.reset();
          this.loader.showSpinner.next(false);
        }, error:
        (_error) => { this.form.reset(); this.alertService.buildDialog().fire({
            title: 'Error!',
            text: "Error occurred while authenticating user.",
            icon: this.alertService.ErrorIcon,
            showCancelButton: false,
            confirmButtonText: "OK",
            cancelButtonText: "",
          });
        }});
  }

  loginSuccess(res){
    let browserSessionId = this.sessionService.getBrowserSession();
    if (!browserSessionId) {
      browserSessionId = Guid.create().toString();
      window.sessionStorage.setItem("browserSessionId", browserSessionId);
    }
    this.tokenService.setToken(res.token, res.refreshToken, this.form.get('username').value).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.tokenService.startOrRestartRefreshTokenTimer();
      
      // Check for stored redirect URL
      const redirectUrl = sessionStorage.getItem('redirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        this.router.navigateByUrl(redirectUrl);
      } else {
        this.router.navigate(['release']);
      }
      this.utilityService.initiateIdleSubcription();
    });
  }

  titleText(res: any) {
    if ((res.validationMessage && res.validationMessageCode && (res.validationMessageCode === 1008 || res.validationMessageCode === 1007))) {
      return 'Alert!';
    }
    else {
      return 'Error!';
    }
  }

  icon(res: any) {
    if ((res.validationMessage && res.validationMessageCode && (res.validationMessageCode === 1008 || res.validationMessageCode === 1007))) {
      return this.alertService.WarningIcon;
    } else {
      return this.alertService.ErrorIcon;
    }
  }

  handleAsyncOperation() {
    if (this.tokenService.isLogged()) {
      this.router.navigate(["release"]);
    }
  }

  getParams() {
    return this.tokenService.getParams();
  }
}
